import React from 'react';
import { useLocation } from 'react-router-dom';
import ClickableText from './ClickableText';
import Button from '../../button';
import { useScrollContext } from './ScrollableTableWrapper';

/**
 * Composant réutilisable pour les cellules de table
 * @param {React.ReactNode} children - Contenu de la cellule
 * @param {string} position - Position de la cellule: 'first', 'last', 'middle' (défaut: 'middle')
 * @param {string} className - Classes CSS additionnelles
 * @param {boolean} clickable - Si true, rend le contenu cliquable avec effet hover
 * @param {function} onClick - Fonction appelée au clic (si clickable est true)
 * @param {string} tooltipText - Texte du tooltip (si clickable est true)
 * @param {string} tooltipPlacement - Placement du tooltip (défaut: 'top')
 * @param {boolean} hasButton - Si true, la cellule contient un bouton
 * @param {string} buttonColor - Couleur du bouton (défaut: 'TABLE')
 * @param {string} buttonSize - Taille du bouton (défaut: 'TABLE')
 * @param {function} buttonOnClick - Fonction appelée au clic du bouton
 * @param {React.ReactNode} buttonContent - Contenu du bouton
 * @param {boolean} stickyLeft - Si true, la cellule est sticky à gauche
 * @param {boolean} isSelected - Si true, la cellule est sélectionnée (fond jaune)
 * @param {number} columnIndex - Index de la colonne (0-based) pour définir des largeurs spécifiques
 */
const TableCell = ({
  children,
  position = 'middle',
  className = '',
  clickable = false,
  onClick,
  tooltipText,
  tooltipPlacement = 'top',
  hasButton = false,
  buttonColor = 'TABLE',
  buttonSize = 'TABLE',
  buttonOnClick,
  buttonContent,
  stickyLeft = false,
  isSelected = false,
  columnIndex,
}) => {
  const location = useLocation();
  const isCompanyPage = location.pathname.includes('company');
  const isQualificationPage = location.pathname.includes('qualification');
  
  // Déterminer le padding selon la position et la page
  const getPaddingClass = () => {
    const pyClass = isCompanyPage ? 'py-3' : 'py-5';
    if (position === 'first') return `${pyClass} pl-4 pr-2`;
    if (position === 'last') return `${pyClass} pl-2 pr-3`;
    return `${pyClass} px-2`;
  };

  const { hasHorizontalScroll = false, hoveredRowId } = useScrollContext();
  const paddingClass = getPaddingClass();
  const baseClasses = `${paddingClass} ${className}`;
  const hasLineClamp = className.includes('line-clamp');
  const lineClampValue = className.match(/line-clamp-(\d+)/)?.[1] || '2';
  
  // Déterminer la classe de fond pour la cellule sticky
  // Si sélectionnée : jaune, si survolée : gris, sinon : blanc
  const getStickyBgClass = () => {
    if (!stickyLeft) return '';
    if (isSelected) return 'bg-yellow-50';
    // Vérifier si la ligne parente est survolée en cherchant dans le DOM
    // Pour simplifier, on utilise hoveredRowId du contexte si disponible
    // Mais comme on n'a pas l'itemId ici, on utilise une approche CSS
    return 'bg-white group-hover:bg-gray-50';
  };
  
  // Déterminer la largeur pour les colonnes de la page company et qualification
  const getWidthClass = () => {
    if (isCompanyPage) {
      // Ne pas appliquer de largeur minimale pour la cellule avec le bouton (position="last")
      if (position === 'last' && hasButton) {
        return 'w-fit';
      }
      if (position === 'first' || columnIndex === 0) {
        return 'min-w-[200px] truncate'; // ENSEIGNE
      }
      if (columnIndex === 1) {
        return 'min-w-[150px] truncate'; // GROUPE
      }
      if (columnIndex === 2) {
        return 'min-w-[150px] truncate'; // REJOINT LE
      }
    }
    if (isQualificationPage) {
      if (position === 'first' || columnIndex === 0) {
        return 'min-w-[200px] truncate'; // NOM DU POSTE
      }
      if (columnIndex === 1) {
        return 'min-w-[150px] truncate'; // SECTEUR
      }
      if (columnIndex === 2) {
        return 'min-w-[150px] truncate'; // CRÉÉ LE
      }
    }
    return '';
  };
  
  const bgClass = getStickyBgClass();
  const widthClass = getWidthClass();
  // Pour les colonnes sticky, on utilise seulement left-0 sans top pour qu'elles défilent verticalement normalement
  const stickyClasses = stickyLeft ? `sticky left-0 z-10 ${bgClass} transition-colors` : '';
  const stickyStyle = stickyLeft && hasHorizontalScroll ? { boxShadow: '2px 0 0 0 rgba(243, 244, 246, 1)' } : {};

  // Si la cellule contient un bouton
  if (hasButton && buttonContent) {
    return (
      <td className={`${baseClasses} ${stickyClasses} ${widthClass}`} style={stickyStyle}>
        <div className="flex justify-center items-center">
          <Button
            color={buttonColor}
            size={buttonSize}
            onClick={(e) => {
              e.stopPropagation();
              if (buttonOnClick) buttonOnClick(e);
            }}
          >
            {buttonContent}
          </Button>
        </div>
      </td>
    );
  }

  // Si la cellule est cliquable
  if (clickable && onClick) {
    return (
      <td className={`${paddingClass} ${stickyClasses} ${widthClass}`} style={stickyStyle}>
        <ClickableText
          onClick={onClick}
          tooltipText={tooltipText}
          tooltipPlacement={tooltipPlacement}
          className={className}
        >
          {children}
        </ClickableText>
      </td>
    );
  }

  // Cellule normale
  if (hasLineClamp) {
    return (
      <td className={`${paddingClass} ${stickyClasses} ${widthClass}`} style={stickyStyle}>
        <div 
          className={className}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: lineClampValue,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word'
          }}
        >
          {children}
        </div>
      </td>
    );
  }

  return <td className={`${baseClasses} ${stickyClasses} ${widthClass}`} style={stickyStyle}>{children}</td>;
};

export default TableCell;

